import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Box, Typography, Grid, Stack, Button, Card, CardContent, Divider, Chip, Drawer,
  List, ListItemButton, ListItemText, TextField
} from "@mui/material";
import { Plus, ArrowRight, Stethoscope, Microscope, Radiation, Pill } from "lucide-react";

const LANES = [
  { key: "waiting",       label: "Waiting Room" },
  { key: "with_mo",       label: "With MO" },
  { key: "in_lab",        label: "In Lab" },
  { key: "in_radiology",  label: "In Radiology" },
  { key: "results_ready", label: "Results Ready" },
  { key: "pharmacy",      label: "Pharmacy" },
  { key: "completed",     label: "Completed" },
];

const STORAGE_KEY = (centre) => `centre_board_state_${centre}`;

function loadState(centre) {
  try {
    const s = JSON.parse(localStorage.getItem(STORAGE_KEY(centre)) || "null") || { seq: 0, tokens: [] };
    s.tokens = (s.tokens || []).map(t => ({
      orders: { lab: false, radiology: false, ...(t.orders || {}) },
      done:   { lab: false, radiology: false, ...(t.done || {}) },
      citizenName: "",
      ...t,
    }));
    return s;
  } catch {
    return { seq: 0, tokens: [] };
  }
}

function saveState(centre, state) { localStorage.setItem(STORAGE_KEY(centre), JSON.stringify(state)); }

function nextId(seq, centre) {
  const code = String(centre || "").split("_").pop() || "C";
  const num = String(seq + 1).padStart(3, "0");
  return `${code}-${num}`;
}

const isMOorCMFM = (role) =>
  ["admin","faculty","senior resident","junior resident","intern","cmfm","medical officer"]
    .some(x => role.includes(x));

const roleCanSee = (role, laneKey) => {
  const r = (role || "").toLowerCase();
  if (isMOorCMFM(r)) return true;
  if (r.includes("deo"))            return laneKey === "waiting";
  if (r.includes("lab technician")) return laneKey === "in_lab";
  if (r.includes("radiographer"))   return laneKey === "in_radiology";
  if (r.includes("pharmacist"))     return laneKey === "pharmacy";
  return false;
};

export default function CentreBoard() {
  const { centreCode } = useParams();
  const user = useMemo(() => JSON.parse(localStorage.getItem('loggedInUser') || 'null') || {}, []);
  const role = String(user?.role || user?.userType || '').toLowerCase();
  const assignedCentre = user?.assignedCentreCode || user?.centreCode || user?.centre || "";

  // If an MO tries to open a different centre, pin them to their own
  useEffect(() => {
    if (role.includes('medical officer') && assignedCentre && assignedCentre !== centreCode) {
      window.location.replace(`/dashboard/centre/${encodeURIComponent(assignedCentre)}`);
    }
  }, [role, assignedCentre, centreCode]);

  const [state, setState] = useState(loadState(centreCode));
  const [drawer, setDrawer] = useState({ open:false, tokenId:null });

  useEffect(() => saveState(centreCode, state), [centreCode, state]);

  const updateToken = (id, updater) => {
    setState(s => ({
      ...s,
      tokens: s.tokens.map(x => x.id === id ? updater({...x}) : x)
    }));
  };

  // Creation paths
  const addTokenByDEO = () => {
    const id = nextId(state.seq, centreCode);
    const t = { id, citizenName: "", status: "waiting", createdAt: Date.now(),
      orders: { lab:false, radiology:false }, done: { lab:false, radiology:false } };
    setState(s => ({ ...s, seq: s.seq + 1, tokens: [t, ...s.tokens] }));
  };

  const addTokenByMO = () => {
    const id = nextId(state.seq, centreCode);
    const t = { id, citizenName: "", status: "with_mo", createdAt: Date.now(),
      orders: { lab:false, radiology:false }, done: { lab:false, radiology:false } };
    setState(s => ({ ...s, seq: s.seq + 1, tokens: [t, ...s.tokens] }));
    setDrawer({ open:true, tokenId:id });
  };

  // Queue helpers
  const laneTokens = (lane) => state.tokens.filter(t => t.status === lane);
  const callNext = () => {
    const waiting = laneTokens("waiting").sort((a,b) => a.createdAt - b.createdAt);
    if (waiting.length) {
      const next = waiting[0];
      updateToken(next.id, (t) => ({ ...t, status: "with_mo" }));
    }
  };

  // MO ordering actions
  const orderLab = (t) => updateToken(t.id, (x) => ({ ...x, orders: { ...x.orders, lab:true }, status: "in_lab" }));
  const orderRadiology = (t) => updateToken(t.id, (x) => ({ ...x, orders: { ...x.orders, radiology:true }, status: "in_radiology" }));
  const orderBoth = (t) => updateToken(t.id, (x) => ({ ...x, orders: { lab:true, radiology:true }, status: "in_lab" }));
  const goToRx = (t) => updateToken(t.id, (x) => ({ ...x, status: "pharmacy" }));

  // Lab/Radiology completion actions
  const markLabResulted = (t) => {
    updateToken(t.id, (x) => {
      const done = { ...x.done, lab:true };
      if (x.orders.radiology && !done.radiology) return { ...x, done, status: "in_radiology" };
      return { ...x, done, status: "results_ready" };
    });
  };
  const markRadiologyResulted = (t) => {
    updateToken(t.id, (x) => {
      const done = { ...x.done, radiology:true };
      if (x.orders.lab && !done.lab) return { ...x, done, status: "in_lab" };
      return { ...x, done, status: "results_ready" };
    });
  };

  // Pharmacy completion
  const dispense = (t) => updateToken(t.id, (x) => ({ ...x, status: "completed" }));

  // lane action buttons per role
  const TokenActions = ({ laneKey, t }) => {
    const r = role;

    if (laneKey === "waiting") {
      if (r.includes("deo")) {
        return <Button size="small" variant="outlined" onClick={() => updateToken(t.id, x => ({ ...x, status:"with_mo" }))} startIcon={<ArrowRight size={14}/>}>Send to MO</Button>;
      }
      if (isMOorCMFM(r)) {
        return <Button size="small" variant="outlined" onClick={() => updateToken(t.id, x => ({ ...x, status:"with_mo" }))} startIcon={<ArrowRight size={14}/>}>Take Patient</Button>;
      }
      return null;
    }

    if (laneKey === "with_mo") {
      if (isMOorCMFM(r)) {
        return (
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={() => setDrawer({ open:true, tokenId: t.id })}>One-Pager</Button>
            <Button size="small" variant="outlined" onClick={() => orderLab(t)} startIcon={<Microscope size={14}/>}>Order Lab</Button>
            <Button size="small" variant="outlined" onClick={() => orderRadiology(t)} startIcon={<Radiation size={14}/>}>Order X-ray</Button>
            <Button size="small" variant="outlined" onClick={() => orderBoth(t)} startIcon={<ArrowRight size={14}/>}>Order Both</Button>
            <Button size="small" variant="contained" onClick={() => goToRx(t)} startIcon={<Pill size={14}/>}>Go to Rx</Button>
          </Stack>
        );
      }
      return null;
    }

    if (laneKey === "in_lab") {
      if (r.includes("lab technician") || isMOorCMFM(r)) {
        return (
          <Stack direction="row" spacing={1}>
            <Button size="small" component={Link} to={`/dashboard/chc-coordination?tab=lab&centre=${encodeURIComponent(centreCode)}`} variant="outlined">Open Lab Panel</Button>
            <Button size="small" variant="outlined" onClick={() => markLabResulted(t)} startIcon={<Microscope size={14}/>}>Mark Resulted</Button>
          </Stack>
        );
      }
      return null;
    }

    if (laneKey === "in_radiology") {
      if (r.includes("radiographer") || isMOorCMFM(r)) {
        return (
          <Stack direction="row" spacing={1}>
            <Button size="small" component={Link} to={`/dashboard/chc-coordination?tab=radiology&centre=${encodeURIComponent(centreCode)}`} variant="outlined">Open Radiology</Button>
            <Button size="small" variant="outlined" onClick={() => markRadiologyResulted(t)} startIcon={<Radiation size={14}/>}>Mark Resulted</Button>
          </Stack>
        );
      }
      return null;
    }

    if (laneKey === "results_ready") {
      if (isMOorCMFM(r)) {
        return (
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={() => setDrawer({ open:true, tokenId: t.id })}>One-Pager</Button>
            <Button size="small" variant="contained" onClick={() => goToRx(t)} startIcon={<Pill size={14}/>}>Finalize Rx → Pharmacy</Button>
          </Stack>
        );
      }
      return null;
    }

    if (laneKey === "pharmacy") {
      if (r.includes("pharmacist") || isMOorCMFM(r)) {
        return (
          <Stack direction="row" spacing={1}>
            <Button size="small" component={Link} to={`/dashboard/chc-coordination?tab=pharmacy&centre=${encodeURIComponent(centreCode)}`} variant="outlined">Open Pharmacy</Button>
            <Button size="small" variant="outlined" onClick={() => dispense(t)} startIcon={<Pill size={14}/>}>Dispense</Button>
          </Stack>
        );
      }
      return null;
    }

    return null;
  };

  return (
    <div className="p-6 min-h-screen rounded-xl shadow border"
         style={{ background: 'var(--mui-palette-background-default)', borderColor: 'var(--mui-palette-divider)' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ color: 'var(--mui-palette-warning-300)' }}>
          {decodeURIComponent(centreCode).replaceAll("_"," ")} — Centre Board
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={addTokenByDEO} startIcon={<Plus size={14}/>}>Register & Token (DEO)</Button>
          <Button size="small" variant="contained" onClick={addTokenByMO} startIcon={<Stethoscope size={14}/>}>Start Visit (MO)</Button>
          {isMOorCMFM(role) && laneTokens("waiting").length > 0 && (
            <Button size="small" variant="outlined" onClick={callNext} startIcon={<ArrowRight size={14}/>}>Call Next</Button>
          )}
        </Stack>
      </Stack>

      <Grid container spacing={2}>
        {LANES.filter(l => roleCanSee(role, l.key)).map((lane) => (
          <Grid key={lane.key} item xs={12} md={6} lg={4}>
            <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">{lane.label}</Typography>
                  <Chip size="small" label={laneTokens(lane.key).length} />
                </Stack>
                <Divider sx={{ mb: 1.5 }} />
                <Stack spacing={1}>
                  {laneTokens(lane.key).map(t => (
                    <Box key={t.id} sx={{ p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.id}</Typography>
                          {(t.orders.lab || t.orders.radiology) && (
                            <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
                              {t.orders.lab && !t.done.lab && <Chip size="small" label="Lab pending" />}
                              {t.orders.radiology && !t.done.radiology && <Chip size="small" label="X-ray pending" />}
                              {(t.done.lab || t.done.radiology) && lane.key !== "pharmacy" && lane.key !== "completed" && t.status !== "with_mo" && (
                                <Chip size="small" label="Some results ready" />
                              )}
                            </Stack>
                          )}
                        </Stack>
                        <TokenActions laneKey={lane.key} t={t} />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* MO One-Pager (stub) */}
      <Drawer anchor="right" open={drawer.open} onClose={() => setDrawer({ open:false, tokenId:null })} PaperProps={{ sx:{ width: 520 }}}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>MO One-Pager — {drawer.tokenId}</Typography>
          <Divider sx={{ mb: 2 }} />
          <List dense>
            {["History","Examination","Vitals","Investigations","Radiology","Prescription","Pharmacy"].map(s => (
              <ListItemButton key={s}><ListItemText primary={s} /></ListItemButton>
            ))}
          </List>
          <Divider sx={{ my: 2 }} />
          <TextField label="Notes (draft)" fullWidth multiline minRows={3} />
        </Box>
      </Drawer>
    </div>
  );
}
